/**
 * @module builder
 */
import type { QuoteDocument, QuoteJobAddress } from 'paintscout'
import addressFormat from 'address-format'
import type { QuoteIndexDocument } from '../../../../database/elasticsearch'
import { getContactAddress } from '../../contact/get-contact-address'

export function getJobAddress(args: {
  quote: QuoteDocument | QuoteIndexDocument
  addressType?: 'shippingAddress' | 'billingAddress'
}) {
  const { quote, addressType } = args
  if (addressType) {
    return quote[addressType] && Object.keys(quote[addressType]).length > 0 ? quote[addressType] : null
  } else {
    return quote.jobAddress && Object.keys(quote.jobAddress).length > 0 ? quote.jobAddress : null
  }
}

/**
 * Picks the four job-address fields off any address-bearing source (a quote/deal `jobAddress`, a
 * `Contact`, etc.) and returns a `QuoteJobAddress` — or null when every field is empty/missing.
 * Used to denormalize a job address onto activities (deals, contacts, tasks, events) so the
 * activity report's Zip column can read it straight from the index.
 */
export function normalizeJobAddress(
  source?: { address?: string | null; city?: string | null; province?: string | null; postal?: string | null } | null
): QuoteJobAddress | null {
  if (!source) {
    return null
  }
  const address = source.address || undefined
  const city = source.city || undefined
  const province = source.province || undefined
  const postal = source.postal || undefined
  if (!address && !city && !province && !postal) {
    return null
  }
  return { address, city, province, postal }
}

/**
 * Returns the quote's job address, falling back to the contact's address when the quote has no
 * street address of its own. Mirrors the long-standing inline logic in `createDealFromQuote` so
 * deal creation, activity indexing, and the activity report's Zip column share one source of
 * truth. Returns null when neither the quote nor the contact has any address fields.
 */
export function getJobAddressWithContactFallback(args: { quote: QuoteDocument }): QuoteJobAddress | null {
  const { quote } = args

  // Prefer the quote's own job address only when it has a street address (matches the deal logic).
  if (quote.jobAddress?.address) {
    return quote.jobAddress
  }

  const contactAddress = getContactAddress(quote.contact)
  if (contactAddress && typeof contactAddress !== 'string') {
    const hasAny = !!(contactAddress.address || contactAddress.city || contactAddress.province || contactAddress.postal)
    if (hasAny) {
      return contactAddress
    }
  }

  // Neither has a street address / contact fields — keep any postal-only job address, else null.
  return getJobAddress({ quote })
}

export function getFormattedJobAddress(args: { quote: QuoteDocument }) {
  const { quote } = args

  if (quote.jobAddress) {
    let text = ''
    if (quote.job_identifier) {
      text += quote.job_identifier + '\n'
    }

    const fullAddress = addressFormat({
      address: quote.jobAddress.address,
      city: quote.jobAddress.city,
      postalCode: quote.jobAddress.postal,
      subdivision: quote.jobAddress.province,
      countryCode: 'US'
    })

    if (fullAddress && fullAddress.length > 0) {
      text += fullAddress.join('\n')
    }

    return text
  }

  return null
}

/**
 * Picks the job-address fields (address/city/province/postal) off any contact-like source
 * (Contact, Company, etc.) and returns a QuoteJobAddress. Centralizes the field mapping so
 * call sites feeding `formatJobAddress` don't each duplicate it, and normalizes null to
 * undefined so sources that use either are accepted.
 */
export function toJobAddress(
  source?: {
    address?: string | null
    city?: string | null
    province?: string | null
    postal?: string | null
  } | null
): QuoteJobAddress {
  return {
    address: source?.address ?? undefined,
    city: source?.city ?? undefined,
    province: source?.province ?? undefined,
    postal: source?.postal ?? undefined
  }
}

export function formatJobAddress(args: { jobAddress?: QuoteJobAddress; jobIdentifier?: string }) {
  const { jobAddress, jobIdentifier } = args
  if (jobAddress) {
    let text = ''
    if (jobIdentifier) {
      text += jobIdentifier + '\n'
    }

    const fullAddress = addressFormat({
      address: jobAddress.address,
      city: jobAddress.city,
      postalCode: jobAddress.postal,
      subdivision: jobAddress.province,
      countryCode: 'US'
    })

    if (fullAddress && fullAddress.length > 0) {
      text += fullAddress.join('\n')
    }

    return text
  }

  return null
}
